import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import * as Yup from 'yup'
import { isEmpty, isEqual, isNil, isNull, isString } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { AbstractForm } from '@/components/forms/formik/AbstractForm'
import ConfirmModal from '@/components/layout/modals/ConfirmModal'
import { ConnectionContentTable } from '@/views/Organizations/components/ConnectionContentTable'
import {
  REPLACE_DONE,
  REPLACE_PATCH_FAILED,
  REPLACE_REMOVE_FAILED,
} from '@/views/Organizations/components/ConnectionContentTable.helpers'
import { ConnectionStatusRow } from '@/views/Organizations/components/ConnectionStatusRow'
import { ManageFilesModal } from '@/views/Organizations/components/ManageFilesModal'
import { offersManageFiles } from '@/views/Organizations/components/ManageFilesModal.helpers'
import { SharedDriveAccessBanner } from '@/views/Organizations/components/SharedDriveAccessBanner'
import { useConnectionDrives } from '@/views/Organizations/components/hooks/useConnectionDrives'
import {
  fetchGoogleDriveServiceAccount,
  createGoogleDriveConnection,
  updateGoogleDriveConnection,
  removeGoogleDriveAllowlistEntry,
} from '@/reducers/organizations/googleDriveConnection.actions'
import {
  ALLOWLIST_SCHEMA,
  ALLOWLIST_ROW_SCHEMA,
  BLANK_ALLOWLIST_ROW,
  extractDriveItemId,
  toFormAllowlist,
  toApiAllowlist,
  normalizedFolder,
  isWholeDrive,
  driveIdOf,
  sameEntry,
  removalImpact,
  persistedRemoveLabel,
  googleDriveManageFilesRoots,
  googleDriveSyncRowsFor,
  SYNC_ERROR_LABELS,
  awaitingDriveAccess,
  syncRowFolderLabel,
  withoutFolderScopes,
} from '@/views/Organizations/components/GoogleDriveConnectionForm.helpers'

// One phrasing for an entry in confirmation copy, so "what exactly is
// affected" reads the same in every dialog.
const entryLabel = (target) =>
  target.wholeDrive
    ? `drive ${target.driveId} (whole drive)`
    : `folder ${target.folderId} in drive ${target.driveId}`

const CONTENT_TABLE_TEST_ID = 'google-drive-content-table'

// What a removal/replacement does to the drive's documents, shared by both
// destructive dialogs. Weaviate nodes carry only the drive id (no folder
// identity), so documents are purged immediately ONLY when the operation
// leaves no allowlist entry for the drive — otherwise the cleanup belongs to
// the next nightly reconcile. The enumeration distinguishes "the count is
// unavailable" (sync state loading/failed — the purge still runs on real
// server data) from "genuinely nothing recorded".
const DriveImpactSummary = ({ impact, syncStateUnavailable, testId }) => (
  <>
    {impact.purgeDeferred && (
      <p>
        Other allowlist entries still cover this drive, so nothing is deleted right now — documents
        the remaining entries no longer select are removed by the next nightly sync. If no other
        entry covers this drive anymore when the removal lands (someone removed it meanwhile), its
        documents are purged immediately instead — the success message reports what actually
        happened.
      </p>
    )}
    {!impact.purgeDeferred && (
      <>
        <p>
          No other allowlist entry covers this drive, so its ingested documents are deleted
          immediately.
        </p>
        {syncStateUnavailable && (
          <p>
            The ingested-document count is unavailable right now — the removal still deletes
            everything this drive has ingested.
          </p>
        )}
        {!syncStateUnavailable && !isNil(impact.driveDocumentCount) && (
          <p data-testid={testId}>{impact.driveDocumentCount} indexed documents are deleted.</p>
        )}
        {!syncStateUnavailable && isNil(impact.driveDocumentCount) && (
          <p>No ingested documents are recorded for this drive yet.</p>
        )}
      </>
    )}
  </>
)

// Destructive confirmation for a persisted-entry removal.
const RemoveEntryConfirmModal = ({
  removeTarget,
  impact,
  syncStateUnavailable,
  removing,
  onConfirm,
  onClose,
}) => (
  <ConfirmModal
    show
    onClose={onClose}
    title={
      impact.purgeDeferred ? 'Remove from allowlist?' : 'Remove from allowlist and purge documents?'
    }
    confirmLabel={impact.purgeDeferred ? 'Remove entry' : 'Remove and purge'}
    busyLabel={impact.purgeDeferred ? 'Removing…' : 'Purging…'}
    busy={removing}
    destructive
    onConfirm={onConfirm}
    testIdPrefix="google-drive-remove-entry"
  >
    {removeTarget.wholeDrive ? (
      <p>
        This removes drive <strong>{removeTarget.driveId}</strong> (whole drive) from the allowlist.
      </p>
    ) : (
      <p>
        This removes folder <strong>{removeTarget.folderId}</strong> in drive{' '}
        <strong>{removeTarget.driveId}</strong> from the allowlist.
      </p>
    )}
    <DriveImpactSummary
      impact={impact}
      syncStateUnavailable={syncStateUnavailable}
      testId="google-drive-remove-entry-affected"
    />
    <p>Re-adding it later re-ingests from scratch on the next sync. This cannot be undone.</p>
  </ConfirmModal>
)

// Destructive confirmation for editing a persisted entry: a replace is the
// removal operation with the new spelling saved first, so what it deletes now
// is exactly what the removal would — computed against the allowlist that
// already includes the new entry (a same-drive rename therefore defers).
// `scopeDropped`: the old entry was a scoped whole-drive row and the new one
// is a folder row, which cannot carry a scope — an isolation setting is about
// to go away, so the dialog has to say so rather than let it happen silently.
const ReplaceEntryConfirmModal = ({
  oldTarget,
  newTarget,
  scopeDropped,
  impact,
  syncStateUnavailable,
  replacing,
  onConfirm,
  onClose,
}) => (
  <ConfirmModal
    show
    onClose={onClose}
    title={
      impact.purgeDeferred ? 'Replace this entry?' : 'Replace this entry and purge its documents?'
    }
    confirmLabel={impact.purgeDeferred ? 'Replace entry' : 'Replace and purge'}
    busyLabel={impact.purgeDeferred ? 'Replacing…' : 'Purging…'}
    busy={replacing}
    destructive
    onConfirm={onConfirm}
    testIdPrefix="google-drive-replace-entry"
  >
    <p>
      This changes <strong>{entryLabel(oldTarget)}</strong> to{' '}
      <strong>{entryLabel(newTarget)}</strong>. What it newly covers ingests on the next sync.
    </p>
    {scopeDropped && (
      <p data-testid="google-drive-replace-entry-scope-dropped">
        Its playbook scope does not carry over: scope applies per drive and a folder row cannot hold
        one, so the drive&apos;s documents answer every playbook after the next sync.
      </p>
    )}
    <DriveImpactSummary
      impact={impact}
      syncStateUnavailable={syncStateUnavailable}
      testId="google-drive-replace-entry-affected"
    />
    <p>This cannot be undone.</p>
  </ConfirmModal>
)

/**
 * One Google Drive connection's detail pane: its status, then the merged
 * content table (allowlist entries carrying their own ingest state), then the
 * connection's details. Create mode when `connection` is null.
 *
 * The list container owns loading and selection, and REMOUNTS this component
 * per selection (AbstractForm has no `enableReinitialize`, so a shared instance
 * would keep the previously selected connection's field values).
 *
 * @param {number} props.organizationId the org being configured
 * @param {Object} props.connection the selected connection, or null to create
 * @param {Object} props.syncState undefined | null | the connection's ingest state
 * @param {Function} props.onSaved awaited after a save/activate; reloads the list
 * @param {Function} props.onClose the form's secondary button action
 * @param {string} props.closeLabel label for that secondary button
 * @param {React.ReactNode} props.footerActions extra footer-left actions
 * @param {Function} props.onDirty reports the form's unsaved-input state upward
 * @param {Function} props.onFormikReady receives the live Formik state each render,
 *   so the container can preserve a draft's values across an unmount
 * @param {Object} props.draftValues create mode only: previously typed values to
 *   restore, when the operator returns to a preserved draft
 * @param {Array} props.playbooks {value: cid, label: name} options for the
 *   per-row playbook scope (QCER-1075); empty until the org's configs load
 * @param {boolean} props.playbooksLoading whether those options are still loading
 * @param {boolean} props.playbooksFailed whether fetching them failed outright
 * @param {boolean} props.supportsManageFiles whether synced rows offer Manage files (set from
 *   the provider registry)
 */
export const GoogleDriveConnectionForm = ({
  organizationId,
  connection,
  syncState,
  onSaved,
  onClose,
  closeLabel = 'Close',
  footerActions = null,
  onDirty,
  onFormikReady,
  draftValues,
  playbooks = [],
  playbooksLoading = false,
  playbooksFailed = false,
  supportsManageFiles = false,
}) => {
  const dispatch = useDispatch()
  // The global service account's email — undefined while fetching, null when
  // the environment isn't bootstrapped yet, string when known. Environment
  // scoped, so a refetch per remount is one cheap GET.
  const [serviceAccountEmail, setServiceAccountEmail] = useState(undefined)
  // A persisted row's inline edit lives outside Formik, so the form tracks it
  // separately: it locks Save (a half-finished replacement must not ride along
  // on a config save) and arms the modal's unsaved-changes guard.
  const [editingEntry, setEditingEntry] = useState(false)
  // The live Formik state, so a save can keep input typed while it was in flight.
  const formikRef = useRef(null)
  // The row whose files Manage files shows: { subtitle, roots }, or null while it is closed.
  const [manageFilesTarget, setManageFilesTarget] = useState(null)

  useEffect(() => {
    let active = true
    // Fire-and-forget like the sync state: the form must never be gated on
    // this hint, and the thunk resolves every failure to null.
    dispatch(fetchGoogleDriveServiceAccount()).then((email) => {
      if (active) {
        setServiceAccountEmail(email)
      }
    })
    return () => {
      active = false
    }
  }, [dispatch])

  const isEdit = !isNil(connection)
  const persistedAllowlist = isEdit ? connection.allowlist : []
  // Blocks belong to the review until Start sync, and the API refuses them (409 `in_review`).
  const inReview = isEdit && !isNil(connection.review_state)
  const drives = useConnectionDrives({
    connectionId: isEdit ? connection.id : null,
    skip: !isEdit || !supportsManageFiles || inReview,
  })

  const handleSave = async (values, { resetForm, setValues }) => {
    // Validation guarantees every row has a Drive and there's at least one;
    // pasted links normalize to bare ids on the way out. Saving activates the
    // connection; the Pause syncing button pauses it independently.
    const allowlist = toApiAllowlist(values.allowlist)
    // The optional Label is a text input, so "unnamed" arrives as ''. Send NULL
    // instead: agent-gpt PATCHes with exclude_unset, so an explicit null clears
    // a name the operator deleted, while omitting it on create lets the column
    // default to NULL rather than storing '' as the connection's name.
    const label = isEmpty(values.label.trim()) ? null : values.label.trim()
    if (isEdit) {
      const updated = await dispatch(
        updateGoogleDriveConnection({
          connectionId: connection.id,
          patch: {
            label,
            workspace_domain: values.workspace_domain,
            allowlist,
            // Saving takes a connection live — EXCEPT on one whose documents
            // were erased: letting a config save activate an offboarded
            // connection would silently re-ingest what was just erased. Going
            // live again is the Resume syncing button's job.
            ...(isNil(connection.purged_at) ? { active: true } : {}),
          },
          successMessage: 'Google Drive connection saved',
        })
      )
      // The form is not remounted after a save, so a saved edit has to move the
      // baseline itself or the close guard keeps warning about it. Input typed
      // while the PATCH was in flight was not saved, so it stays dirty.
      if (!isNil(updated)) {
        const liveValues = formikRef.current.values
        resetForm({ values })
        if (!isEqual(liveValues, values)) {
          setValues(liveValues)
        }
      }
      await onSaved()
      return
    }
    const created = await dispatch(
      createGoogleDriveConnection({
        organizationId,
        label: label ?? undefined,
        workspaceDomain: values.workspace_domain,
        allowlist,
        active: true,
      })
    )
    // Select the connection just created rather than whatever sorts first. A
    // failed create (null) leaves the selection alone; the thunk has already
    // toasted it.
    await onSaved(created?.id)
  }

  const handleToggleActive = async () => {
    const goingLive = !connection.active
    if (
      goingLive &&
      // eslint-disable-next-line no-alert
      !window.confirm(
        `Turn on syncing? Each Shared Drive below must already be shared with ${
          isString(serviceAccountEmail) ? serviceAccountEmail : 'the Balto service account'
        } as a Viewer.`
      )
    ) {
      return
    }
    await dispatch(
      updateGoogleDriveConnection({
        connectionId: connection.id,
        patch: { active: goingLive },
        successMessage: goingLive ? 'Connection activated' : 'Connection deactivated',
      })
    )
    await onSaved()
  }

  // The replace runs additive-first: the new spelling is saved alongside the
  // old entry, THEN the old one is removed through the dedicated removal
  // operation — which purges immediately only if the remaining entries (the
  // new one included) no longer cover the drive. Every failure mode leaves the
  // server self-consistent: a failed PATCH changed nothing, and a failed
  // removal leaves old + new both configured with nothing purged.
  const replaceEntry = async ({ oldEntry, newEntry }) => {
    const patched = await dispatch(
      updateGoogleDriveConnection({
        connectionId: connection.id,
        patch: {
          allowlist: [...withoutFolderScopes(connection.allowlist), ...toApiAllowlist([newEntry])],
        },
        successMessage: 'New entry saved',
      })
    )
    if (isNil(patched)) {
      return REPLACE_PATCH_FAILED
    }
    const removed = await dispatch(
      removeGoogleDriveAllowlistEntry({
        connectionId: connection.id,
        driveId: driveIdOf(oldEntry),
        folderId: isWholeDrive(oldEntry.folder_id)
          ? undefined
          : normalizedFolder(oldEntry.folder_id),
      })
    )
    return isNil(removed) ? REPLACE_REMOVE_FAILED : REPLACE_DONE
  }

  // Names the row a per-row control belongs to; see the SharePoint twin for
  // why a static label is not enough on a table that repeats its controls.
  const rowControlLabel = (label, row) => {
    const driveId = extractDriveItemId(row.drive_id)
    if (isEmpty(driveId)) {
      return `${label} for this row`
    }
    const folderId = extractDriveItemId(row.folder_id)
    return isEmpty(folderId)
      ? `${label} for drive ${driveId}`
      : `${label} for folder ${folderId} in drive ${driveId}`
  }

  const targetOf = (entry) => ({
    driveId: driveIdOf(entry),
    folderId: normalizedFolder(entry.folder_id),
    wholeDrive: isWholeDrive(entry.folder_id),
  })

  // Typed values canonicalize before they become an identity: pasted Drive
  // links extract to bare ids (the server rejects URLs), so "the same drive by
  // its link" compares equal to the stored id.
  const normalizeEntry = (entry) => ({
    drive_id: extractDriveItemId(entry.drive_id),
    folder_id: extractDriveItemId(entry.folder_id),
  })

  const sameNormalizedEntry = (entryA, entryB) =>
    sameEntry(normalizeEntry(entryA), normalizeEntry(entryB))

  // A drive that never resolved, or is neither synced nor stamped, has nothing to show; a connection in review offers none.
  const manageFilesActions = (row, covered, index) => {
    const roots = googleDriveManageFilesRoots(covered)
    if (inReview || !offersManageFiles(roots, drives)) {
      return []
    }
    return [
      {
        label: 'Manage files',
        onClick: () => setManageFilesTarget({ subtitle: entryLabel(targetOf(row)), roots }),
        testId: `${CONTENT_TABLE_TEST_ID}-manage-files-${index}`,
      },
    ]
  }

  const schema = [
    {
      raw: true,
      name: 'allowlist',
      defaultValue: [{ ...BLANK_ALLOWLIST_ROW }],
      customValidators: [ALLOWLIST_SCHEMA],
      as: (
        <ConnectionContentTable
          key="allowlist"
          connectionId={isEdit ? connection.id : null}
          persistedAllowlist={persistedAllowlist}
          syncState={syncState}
          title="Drive content"
          caption={
            'Leave folder blank to sync a whole Shared Drive — drive and folder links ' +
            'paste straight from Google Drive. Playbooks limits which playbooks a ' +
            "whole-drive row's documents answer; leave it empty and they answer every " +
            'playbook. Scope applies per drive, so a folder row cannot carry one.'
          }
          notice={
            isEdit &&
            connection.active &&
            awaitingDriveAccess(syncState, persistedAllowlist) && (
              <div className="mbs">
                <SharedDriveAccessBanner serviceAccountEmail={serviceAccountEmail} />
              </div>
            )
          }
          columns={[
            { name: 'drive_id', header: 'Drive', placeholder: 'Drive id or link', showError: true },
            {
              name: 'folder_id',
              header: 'Folder',
              placeholder: 'Whole drive — leave blank',
              showError: true,
              blankLabel: 'Whole drive',
            },
            {
              name: 'config_cids',
              header: 'Playbooks',
              control: 'multiselect',
              options: playbooks,
              loading: playbooksLoading,
              // Locked whenever the options are absent — failed OR still
              // loading. They are what renders the saved scope, so without
              // them the control shows nothing selected while the row still
              // holds its cids: an operator editing that would believe they
              // are setting scope from scratch and instead append to a scope
              // they cannot see.
              disabled: playbooksFailed || playbooksLoading,
              blankLabel: 'All playbooks',
              // Ingest applies scope per drive, so a folder row has nowhere to
              // carry one and the server refuses it there (QCER-1123).
              disabledWhen: (row) => !isWholeDrive(row.folder_id),
              placeholderOf: (row) => {
                if (!isWholeDrive(row.folder_id)) {
                  return 'Whole-drive rows only'
                }
                return playbooksFailed ? 'Playbooks unavailable' : 'All playbooks'
              },
              ariaLabelOf: (row) => rowControlLabel('Playbooks answered by', row),
              // Confirmed in place rather than through the replace flow — see
              // the SharePoint form for why scope cannot make config and data
              // drift.
              carried: true,
            },
          ]}
          blankRow={BLANK_ALLOWLIST_ROW}
          addLabel="Add drive"
          entrySchema={ALLOWLIST_ROW_SCHEMA}
          sameEntry={sameNormalizedEntry}
          normalizeEntry={normalizeEntry}
          syncRowsFor={googleDriveSyncRowsFor}
          errorLabels={SYNC_ERROR_LABELS}
          rowLabel={syncRowFolderLabel}
          onEditingChange={setEditingEntry}
          onRemoved={onSaved}
          draftRemoveLabel="Remove drive"
          persistedRemoveLabel={(row) => persistedRemoveLabel(persistedAllowlist, row)}
          removeTargetOf={targetOf}
          removeEntry={(target) =>
            dispatch(
              removeGoogleDriveAllowlistEntry({
                connectionId: connection.id,
                driveId: target.driveId,
                folderId: target.wholeDrive ? undefined : target.folderId,
              })
            )
          }
          survivesTarget={(row, target) =>
            sameNormalizedEntry(row, { drive_id: target.driveId, folder_id: target.folderId })
          }
          renderRemoveConfirmModal={({ target, removing, onConfirm, onClose: onModalClose }) => (
            <RemoveEntryConfirmModal
              removeTarget={target}
              impact={removalImpact(syncState, persistedAllowlist, target.driveId, target.folderId)}
              syncStateUnavailable={isNil(syncState)}
              removing={removing}
              onConfirm={onConfirm}
              onClose={onModalClose}
            />
          )}
          replaceEntry={replaceEntry}
          renderReplaceConfirmModal={({
            oldEntry,
            newEntry,
            replacing,
            onConfirm,
            onClose: onModalClose,
          }) => (
            <ReplaceEntryConfirmModal
              oldTarget={targetOf(oldEntry)}
              newTarget={targetOf(newEntry)}
              scopeDropped={!isEmpty(oldEntry.config_cids) && !isWholeDrive(newEntry.folder_id)}
              // The impact of removing the OLD entry once the NEW one is
              // already saved: a same-drive rename correctly reads as deferred.
              impact={removalImpact(
                syncState,
                [...persistedAllowlist, ...toApiAllowlist([newEntry])],
                driveIdOf(oldEntry),
                normalizedFolder(oldEntry.folder_id)
              )}
              syncStateUnavailable={isNil(syncState)}
              replacing={replacing}
              onConfirm={onConfirm}
              onClose={onModalClose}
            />
          )}
          rowActions={supportsManageFiles ? manageFilesActions : undefined}
          testId={CONTENT_TABLE_TEST_ID}
        />
      ),
    },
    {
      isStatic: true,
      name: 'connection_details_heading',
      label: 'Connection details',
    },
    {
      type: 'text',
      name: 'label',
      label: 'Label',
      placeholder: 'Contoso EMEA',
      labelTooltip:
        'Optional name for this connection, shown in the connections list. Leave it blank and ' +
        'the list falls back to the workspace domain.',
      customValidators: [Yup.string().max(100, 'Label must be 100 characters or fewer')],
    },
    {
      type: 'text',
      name: 'workspace_domain',
      label: 'Workspace domain',
      placeholder: 'yourcompany.com',
      labelTooltip: 'Your Google Workspace domain. Optional, for display only.',
    },
  ]

  const existingValues = isEdit
    ? {
        // The server returns null for an unnamed connection; Formik needs ''
        // or the input renders uncontrolled.
        label: connection.label ?? '',
        workspace_domain: connection.workspace_domain || '',
        allowlist: toFormAllowlist(connection.allowlist),
      }
    : // A preserved draft's typed values; undefined starts the form blank.
      draftValues

  return (
    <>
      {/* The identity customers grant access to: one global service account,
          shared drives are the access boundary. Nothing to configure per org;
          the customer reads this email and shares their drives with it. */}
      {isString(serviceAccountEmail) && (
        <div className="field-help small-text mbs" data-testid="google-drive-service-account">
          Share each Shared Drive with <strong>{serviceAccountEmail}</strong> as a Viewer (Manage
          members) before you turn on syncing.
        </div>
      )}
      {isNull(serviceAccountEmail) && (
        <div className="field-help small-text mbs" data-testid="google-drive-service-account">
          Couldn&apos;t load the Balto service account to share your drives with. Contact Balto
          support.
        </div>
      )}
      {isEdit && (
        <ConnectionStatusRow
          connection={connection}
          syncState={syncState}
          onReviewClosed={onSaved}
        />
      )}
      <AbstractForm
        schema={schema}
        onSubmit={handleSave}
        onClose={onClose}
        // A half-finished inline edit counts as unsaved input for the modal's
        // close guard, alongside Formik's own dirty state.
        onDirty={(dirty) => onDirty?.(dirty || editingEntry)}
        onFormikReady={(formik) => {
          formikRef.current = formik
          onFormikReady?.(formik)
        }}
        isModal
        buttonLabel="Save"
        buttonProps={
          editingEntry ? { disabled: true, disabledTooltip: 'Finish editing the entry first' } : {}
        }
        closeButtonLabel={closeLabel}
        footerStart={
          isEdit ? (
            <>
              <Button
                type="button"
                critical={connection.active}
                brand={!connection.active}
                onClick={handleToggleActive}
              >
                {connection.active ? 'Pause syncing' : 'Resume syncing'}
              </Button>
              {footerActions}
            </>
          ) : null
        }
        existingValues={existingValues}
      />
      {/* Mounted only while open: a save that outlives the modal reports by toast, not onClose. */}
      {!isNil(manageFilesTarget) && (
        <ManageFilesModal
          connectionId={connection.id}
          subtitle={manageFilesTarget.subtitle}
          roots={manageFilesTarget.roots}
          onClose={() => setManageFilesTarget(null)}
        />
      )}
    </>
  )
}
